import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import {
  downloadInvoicePdf,
  fetchInvoiceByAppointment,
  fetchInvoiceByOrder,
  openInvoicePdf,
} from '../../api/invoices';
import { useToast } from '../../context/ToastContext';
import { formatLkr } from '../../utils/pricingDisplay';

const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const fetchInvoicePdfBlob = async (invoiceId) => {
  const response = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
  return response.data;
};

const InvoiceActions = ({ kind, entityId, enabled = true, className = '' }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const queryFn = kind === 'order'
    ? () => fetchInvoiceByOrder(entityId)
    : () => fetchInvoiceByAppointment(entityId);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['invoice', kind, entityId],
    queryFn,
    enabled: enabled && !!entityId,
    retry: false,
  });

  const invoice = data?.invoice;

  const downloadMut = useMutation({
    mutationFn: () => downloadInvoicePdf(invoice._id, invoice.invoiceNumber),
    onError: () => showToast(t('invoice.downloadFailed'), 'error'),
  });

  const printMut = useMutation({
    mutationFn: () => openInvoicePdf(invoice._id),
    onError: () => showToast(t('invoice.printFailed'), 'error'),
  });

  const shareMut = useMutation({
    mutationFn: async () => {
      const s = invoice.snapshot || {};
      const text = t('invoice.shareText', {
        number: invoice.invoiceNumber,
        total: formatLkr(s.total),
        seller: s.seller?.businessName || '',
      });
      const title = t('invoice.shareTitle', { number: invoice.invoiceNumber });

      if (navigator.share) {
        try {
          const blob = await fetchInvoicePdfBlob(invoice._id);
          const file = new File([blob], `${invoice.invoiceNumber}.pdf`, { type: 'application/pdf' });
          if (navigator.canShare?.({ files: [file] })) {
            await navigator.share({ title, text, files: [file] });
            return;
          }
          await navigator.share({ title, text, url: window.location.href });
          return;
        } catch (err) {
          if (err?.name === 'AbortError') return;
        }
      }

      await copyToClipboard(text);
      showToast(t('invoice.copied'), 'success');
    },
    onError: (err) => {
      if (err?.name === 'AbortError') return;
      showToast(t('invoice.shareFailed'), 'error');
    },
  });

  if (!enabled) return null;

  if (isLoading) {
    return <p className={`text-sm text-zinc-500 ${className}`}>{t('common.loading')}</p>;
  }

  if (isError) {
    return (
      <button
        type="button"
        onClick={() => refetch()}
        className={`text-sm text-glow-400 underline ${className}`}
      >
        {t('invoice.loadInvoice')}
      </button>
    );
  }

  if (!invoice) return null;

  return (
    <div className={className}>
      <p className="text-xs text-zinc-500">{t('invoice.number', { number: invoice.invoiceNumber })}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => downloadMut.mutate()}
          disabled={downloadMut.isPending}
          className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          {t('invoice.download')}
        </button>
        <button
          type="button"
          onClick={() => printMut.mutate()}
          disabled={printMut.isPending}
          className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          {t('invoice.print')}
        </button>
        <button
          type="button"
          onClick={() => openInvoicePdf(invoice._id)}
          className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-sm hover:bg-zinc-100 dark:hover:bg-zinc-800"
        >
          {t('invoice.view')}
        </button>
        <button
          type="button"
          onClick={() => shareMut.mutate()}
          disabled={shareMut.isPending}
          className="rounded-lg border border-glow-600/50 px-3 py-1.5 text-sm text-glow-300 hover:bg-glow-600/10"
        >
          {t('invoice.share')}
        </button>
      </div>
    </div>
  );
};

export default InvoiceActions;
